import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Check, Loader2, Clock, AlertTriangle, CircleDashed, PlugZap,
  RefreshCw, FileText, Layers, Users, Image as ImageIcon, ShieldCheck, Trash2,
  DollarSign, ChevronDown, RotateCw, Copy, ArrowUp, ArrowDown, Radio, Send, Download, Clapperboard,
  Sparkles, ArrowRight,
} from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, Pill, Spinner, cx, CinematicStage, RoomBackdrop, PipelineRail } from "@/components/shared";
import { PRODUCTION_JOURNEY, ROOM_BACKDROPS } from "@/lib/studio";
import { iconFor } from "@/lib/agentIcons";
import { StatusPill } from "@/pages/Home";
import PreviewPlayer from "@/components/PreviewPlayer";
import AIReview from "@/components/AIReview";
import ProductionCenter from "@/components/ProductionCenter";
import { PlayCircle, Wand2, Rocket } from "lucide-react";

const STAGE_ICON = { done: Check, running: Loader2, pending: CircleDashed, skipped: CircleDashed, waiting_provider: PlugZap, failed: AlertTriangle };

function StageRow({ stage, onRetry }) {
  const [open, setOpen] = useState(false);
  const Icon = STAGE_ICON[stage.status] || CircleDashed;
  const color = {
    done: "text-emerald-400", running: "text-[#FFB300]", failed: "text-red-400",
    waiting_provider: "text-[#5B4DFF]", pending: "text-slate-600", skipped: "text-slate-500",
  }[stage.status];
  const hasLogs = (stage.logs || []).length > 0;
  return (
    <div className="py-2.5">
      <div className="flex items-start gap-3">
        <div className={cx("mt-0.5", color)}>
          <Icon className={cx("w-[18px] h-[18px]", stage.status === "running" && "animate-spin")} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cx("text-sm", stage.status === "pending" ? "text-slate-500" : "text-white")}>{stage.label}</span>
            {stage.agent && <span className="text-[10px] font-mono text-[#b6aaff]">· {stage.agent}</span>}
            {!stage.real && <span className="text-[10px] font-mono text-slate-500 border border-white/10 rounded px-1">provider</span>}
          </div>
          {stage.note && <div className="text-xs text-slate-500 mt-0.5">{stage.note}</div>}
          <div className="flex items-center gap-3 mt-1">
            {stage.eta ? <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> ~{stage.eta}s</span> : null}
            {stage.cost ? <span className="text-[10px] text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${stage.cost}</span> : null}
            {hasLogs && <button onClick={() => setOpen((v) => !v)} className="text-[10px] text-slate-400 hover:text-white flex items-center gap-1"><ChevronDown className={cx("w-3 h-3 transition-transform", open && "rotate-180")} /> logs</button>}
            {stage.status === "failed" && onRetry && <button onClick={onRetry} className="text-[10px] text-[#FFB300] flex items-center gap-1"><RotateCw className="w-3 h-3" /> retry</button>}
          </div>
          {open && hasLogs && (
            <div className="mt-2 rounded-lg bg-black/30 border border-white/5 p-2 space-y-1 font-mono">
              {stage.logs.map((l, i) => (
                <div key={i} className="text-[10px] text-slate-400">
                  <span className="text-slate-600">{new Date(l.t).toLocaleTimeString()}</span> {l.msg}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "overview", label: "Overview", icon: FileText },
  { id: "preview", label: "Preview", icon: PlayCircle },
  { id: "ai-review", label: "AI Review", icon: Wand2 },
  { id: "script", label: "Script", icon: FileText },
  { id: "storyboard", label: "Storyboard", icon: Layers },
  { id: "scenes", label: "Scene Manager", icon: Layers },
  { id: "characters", label: "Characters", icon: Users },
  { id: "thumbnails", label: "Thumbnails", icon: ImageIcon },
  { id: "quality", label: "Quality", icon: ShieldCheck },
  { id: "clips", label: "Clips", icon: Clapperboard },
  { id: "audio", label: "Audio", icon: Radio },
  { id: "exports", label: "Exports", icon: Send },
  { id: "production", label: "Production Center", icon: Rocket },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proj, setProj] = useState(null);
  const [tab, setTab] = useState("overview");
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProj(data);
      return data.status;
    } catch {
      toast.error("Project not found");
      navigate("/projects");
    }
  };

  useEffect(() => {
    let active = true;
    const poll = async () => {
      const status = await load();
      if (active && (status === "queued" || status === "generating")) {
        timer.current = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { active = false; clearTimeout(timer.current); };
  }, [id]); // eslint-disable-line

  const [regenerating, setRegenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const regen = async () => {
    // SAFETY (hardening gate Phase 20): guards a duplicate/double-click
    // "Regenerate" from firing a second request while the first is still
    // in flight. The backend (routes/projects.py::regenerate_stage) now
    // also rejects a duplicate regenerate while one is already active --
    // this guard is what turns that into a clean, immediate no-op instead
    // of a second real request the user has to wait on just to be told no.
    if (regenerating) return;
    setRegenerating(true);
    try {
      await api.post(`/projects/${id}/regenerate/full`);
      toast.success("Regeneration started");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not regenerate"); }
    finally { setRegenerating(false); }
  };

  const del = async () => {
    // SAFETY (hardening gate Phase 20): this permanently deletes the
    // project AND wipes every generated artifact from storage (see
    // routes/projects.py::delete_project) -- there is no undo. This used
    // to fire immediately on a single click with no confirmation, so a
    // stray click on this small icon button was an unrecoverable
    // data-loss event.
    if (deleting) return;
    if (!window.confirm("Delete this project and all its generated media? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      navigate("/projects");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete project");
      setDeleting(false);
    }
  };

  if (!proj) return <Spinner label="Entering the film world…" />;

  const a = proj.artifacts || {};
  const jTotal = PRODUCTION_JOURNEY.length;
  const jDone = Math.floor(((proj.progress || 0) / 100) * jTotal);
  const doneKeys = PRODUCTION_JOURNEY.slice(0, jDone).map((s) => s.key);
  const activeKey = proj.status === "ready"
    ? "export"
    : PRODUCTION_JOURNEY[Math.min(jDone, jTotal - 1)].key;
  const coverImg = proj.cover ? mediaUrl(proj.cover) : ROOM_BACKDROPS.writers;

  return (
    <CinematicStage>
      <button onClick={() => navigate("/projects")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6" data-testid="back-to-projects">
        <ArrowLeft className="w-4 h-4" /> Film Library
      </button>

      <RoomBackdrop image={coverImg} className="letterbox p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <StatusPill status={proj.status} />
              <span className="text-xs text-slate-400 font-mono">{proj.config?.style} · {proj.config?.duration} · {proj.config?.aspect_ratio}</span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">{proj.title}</h1>
            <p className="text-slate-300 mt-2 max-w-2xl text-sm italic">"{proj.prompt}"</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={regen} disabled={regenerating} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-slate-200 hover:text-white text-sm backdrop-blur disabled:opacity-50 disabled:cursor-not-allowed" data-testid="regenerate-button">
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate
            </button>
            <button onClick={del} disabled={deleting} className="p-2 rounded-xl bg-white/10 border border-white/15 text-slate-300 hover:text-red-400 backdrop-blur disabled:opacity-50 disabled:cursor-not-allowed" data-testid="delete-project-button">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="mt-6 pt-5 border-t border-white/10">
          <PipelineRail doneKeys={doneKeys} activeKey={activeKey} compact />
        </div>
      </RoomBackdrop>

      {proj.status === "blocked_insufficient_credits" && (
        <GlassCard className="p-5 mb-6 flex items-start gap-4 border-[#FFB300]/40" data-testid="blocked-credits-banner">
          <div className="w-11 h-11 rounded-xl bg-[#FFB300]/15 border border-[#FFB300]/40 grid place-items-center shrink-0">
            <Sparkles className="w-5 h-5 text-[#FFB300]" />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-semibold text-white">Not enough credits to produce this film</div>
            <div className="text-sm text-slate-300 mt-1">
              This production needs about <span className="text-[#FFB300] font-semibold">{(proj.block_reason?.required || 0).toLocaleString()}</span> credits
              but your balance is <span className="text-[#FFB300] font-semibold">{(proj.block_reason?.balance || 0).toLocaleString()}</span>.
              No credits were charged — add a credit pack to run it.
            </div>
            <button onClick={() => navigate("/billing")} data-testid="blocked-buy-credits"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white text-sm font-semibold transition glow-purple">
              Buy credits <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </GlassCard>
      )}

      {(proj.status === "generating" || proj.status === "queued") && proj.active_agent && (
        <GlassCard className="p-4 mb-6 flex items-center gap-4 border-[#5B4DFF]/30">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#5B4DFF] to-[#FFB300] grid place-items-center pulse-ring">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-white font-medium text-sm">{proj.active_agent} is working…</div>
            <div className="text-xs text-slate-400">{proj.current_stage?.replace(/_/g, " ")}</div>
          </div>
          <Loader2 className="w-5 h-5 animate-spin text-[#FFB300]" />
        </GlassCard>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pipeline */}
        <GlassCard className="p-6 lg:sticky lg:top-24 self-start">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Production Timeline</h3>
            <span className="font-mono text-sm text-[#FFB300]">{proj.progress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-4">
            <motion.div className="h-full bg-gradient-to-r from-[#5B4DFF] to-[#FFB300]" animate={{ width: `${proj.progress}%` }} />
          </div>
          <div className="divide-y divide-white/5">
            {(proj.pipeline || []).map((s) => <StageRow key={s.id} stage={s} onRetry={regen} />)}
          </div>
        </GlassCard>

        {/* Artifacts */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex gap-2 flex-wrap">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`project-tab-${t.id}`}
                className={cx("inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition",
                  tab === t.id ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/50 text-white" : "bg-white/5 border-white/10 text-slate-400 hover:text-white")}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" && <Overview plan={proj.plan} research={a.research} sceneImgs={a.scene_images} />}
          {tab === "preview" && <PreviewPlayer proj={proj} onChange={load} />}
          {tab === "ai-review" && <AIReview pid={proj.id} onChange={load} />}
          {tab === "script" && <ScriptView script={a.script} />}
          {tab === "storyboard" && <Storyboard board={a.storyboard} images={a.scene_images} />}
          {tab === "scenes" && <SceneManager pid={proj.id} board={a.storyboard} images={a.scene_images} onChange={load} />}
          {tab === "characters" && <Characters data={a.character} />}
          {tab === "thumbnails" && <Thumbnails data={a.thumbnails} />}
          {tab === "quality" && <Quality data={a.quality} />}
          {tab === "clips" && <Clips pid={proj.id} data={a.scene_videos} onChange={load} />}
          {tab === "audio" && <AudioTab voiceover={a.voiceover} music={a.music} />}
          {tab === "exports" && <Exports pid={proj.id} />}
          {tab === "production" && <ProductionCenter pid={proj.id} proj={proj} />}
        </div>
      </div>
    </CinematicStage>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-2">{title}</div>
      {children}
    </div>
  );
}

function Pending() {
  return <GlassCard className="p-8 text-center text-slate-500">Generating… this section will appear soon.</GlassCard>;
}

function Overview({ plan, research, sceneImgs }) {
  return (
    <GlassCard className="p-6 space-y-5">
      {plan?.story_summary && <Section title="Story Summary"><p className="text-slate-200 leading-relaxed">{plan.story_summary}</p></Section>}
      {research ? (
        <>
          <Section title="Research Overview"><p className="text-slate-300 text-sm leading-relaxed">{research.overview}</p></Section>
          {research.key_facts?.length > 0 && (
            <Section title="Key Facts">
              <ul className="space-y-1.5">{research.key_facts.map((f, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-[#5B4DFF]">◆</span> {f}</li>)}</ul>
            </Section>
          )}
          {research.story_arc && <Section title="Story Arc"><p className="text-slate-300 text-sm">{research.story_arc}</p></Section>}
        </>
      ) : <Pending />}
    </GlassCard>
  );
}

function ScriptView({ script }) {
  if (!script) return <Pending />;
  const parts = [
    ["Hook", script.hook], ["Introduction", script.introduction], ["Body", script.body],
    ["Narration", script.narration], ["Ending", script.ending], ["Call to Action", script.call_to_action],
  ].filter(([, v]) => v);
  return (
    <GlassCard className="p-6 space-y-5">
      {parts.map(([k, v]) => <Section key={k} title={k}><p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{v}</p></Section>)}
    </GlassCard>
  );
}

function Storyboard({ board, images }) {
  if (!board?.scenes) return <Pending />;
  const imgMap = {};
  (images?.images || []).forEach((i) => { imgMap[i.index] = i.url; });
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {board.scenes.map((s) => (
        <GlassCard key={s.index} hover className="overflow-hidden">
          <div className="aspect-video bg-[#0A1435]">
            {imgMap[s.index] ? <img src={mediaUrl(imgMap[s.index])} alt={s.title} className="w-full h-full object-cover" /> :
              <div className="w-full h-full grid place-items-center text-slate-600 shimmer"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white font-medium text-sm">Scene {s.index}: {s.title}</span>
              <span className="text-xs text-slate-500">{s.duration_seconds}s</span>
            </div>
            <p className="text-xs text-slate-400 line-clamp-2">{s.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[s.camera_angle, s.lighting, s.emotion].filter(Boolean).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-400">{t}</span>)}
            </div>
          </div>
        </GlassCard>
      ))}
    </div>
  );
}

function Characters({ data }) {
  if (!data) return <Pending />;
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        {(data.images || []).map((c, i) => (
          <GlassCard key={i} className="overflow-hidden">
            <div className="aspect-square bg-[#0A1435]">
              {c.url ? <img src={mediaUrl(c.url)} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-600"><Users className="w-8 h-8" /></div>}
            </div>
            <div className="p-4"><div className="text-white font-medium">{c.name}</div><div className="text-xs text-slate-400 mt-1">{c.description}</div></div>
          </GlassCard>
        ))}
      </div>
      <GlassCard className="p-5">
        <Section title="Character Memory (consistency)">
          <div className="space-y-2">{(data.characters || []).map((c, i) => <div key={i} className="text-sm text-slate-300"><span className="text-white">{c.name}</span> — {c.description}</div>)}</div>
        </Section>
      </GlassCard>
    </div>
  );
}

function Thumbnails({ data }) {
  if (!data) return <Pending />;
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      {(data.thumbnails || []).map((t, i) => (
        <GlassCard key={i} hover className="overflow-hidden">
          <div className="aspect-video bg-[#0A1435]">
            {t.url ? <img src={mediaUrl(t.url)} alt={`Thumbnail ${t.variant}`} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon className="w-6 h-6" /></div>}
          </div>
          <div className="p-3 text-center text-xs text-slate-400">Option {t.variant}</div>
        </GlassCard>
      ))}
    </div>
  );
}

const SCORE_LABELS = {
  story: "Story", visual: "Visual", brand_consistency: "Brand Consistency",
  audience_retention: "Audience Retention", educational_value: "Educational Value",
  thumbnail_strength: "Thumbnail Strength", voice_quality: "Voice Quality",
};

function Quality({ data }) {
  if (!data) return <Pending />;
  const scores = data.scores || {};
  const barColor = (v) => v >= 80 ? "#22c55e" : v >= 60 ? "#FFB300" : v > 0 ? "#f97316" : "#475569";
  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <div className="flex items-center gap-6">
          <div className="relative w-28 h-28 shrink-0">
            <svg viewBox="0 0 36 36" className="w-28 h-28 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#FFB300" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${data.overall || 0}, 100`} />
            </svg>
            <div className="absolute inset-0 grid place-items-center text-center">
              <div><div className="font-display text-3xl font-bold">{data.overall || 0}</div><div className="text-[10px] text-slate-500">/ 100</div></div>
            </div>
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300]">Overall Production Score</div>
            <h3 className="font-display text-2xl font-bold mt-1">{data.overall >= 80 ? "Excellent" : data.overall >= 60 ? "Solid" : "Needs work"}</h3>
            <div className="mt-2">{data.passed ? <Pill tone="green"><Check className="w-3 h-3" /> Quality Passed</Pill> : <Pill tone="gold"><AlertTriangle className="w-3 h-3" /> Improvements available</Pill>}</div>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-4">Score Breakdown</div>
        <div className="space-y-3">
          {Object.entries(scores).map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-xs mb-1"><span className="text-slate-300">{SCORE_LABELS[k] || k}</span><span className="font-mono" style={{ color: barColor(v) }}>{v === 0 ? "—" : v}</span></div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v}%`, background: barColor(v) }} /></div>
            </div>
          ))}
        </div>
      </GlassCard>

      {data.suggestions?.length > 0 && (
        <GlassCard className="p-6">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-3">Suggestions for improvement</div>
          <ul className="space-y-2">
            {data.suggestions.map((s, i) => <li key={i} className="flex gap-2 text-sm text-slate-300"><AlertTriangle className="w-4 h-4 text-[#FFB300] shrink-0 mt-0.5" /> {s}</li>)}
          </ul>
        </GlassCard>
      )}
      {scores.voice_quality === 0 && (
        <div className="text-xs text-slate-500 px-1">Voice score shows “—” for projects created before the voice engine was enabled. Regenerate to score narration.</div>
      )}
    </div>
  );
}

function SceneManager({ pid, board, images, onChange }) {
  const [scenes, setScenes] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!board?.scenes) { setScenes(null); return; }
    const imgMap = {};
    (images?.images || []).forEach((i) => { imgMap[i.index] = i.url; });
    setScenes(board.scenes.map((s) => ({ ...s, _src_index: s.index, url: imgMap[s.index] || null })));
  }, [board, images]);

  if (!scenes) return <Pending />;

  const persist = async (next) => {
    setScenes(next);
    await api.put(`/projects/${pid}/scenes`, { scenes: next.map(({ url, ...s }) => s) });
  };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= scenes.length) return;
    const next = [...scenes]; [next[i], next[j]] = [next[j], next[i]]; persist(next);
  };
  const dup = (i) => { const next = [...scenes]; next.splice(i + 1, 0, { ...scenes[i], _src_index: scenes[i]._src_index }); persist(next); toast.success("Scene duplicated"); };
  const del = (i) => { if (scenes.length <= 1) return toast.error("Keep at least one scene"); const next = scenes.filter((_, idx) => idx !== i); persist(next); toast.success("Scene deleted"); };
  const editField = (i, k, v) => { const next = [...scenes]; next[i] = { ...next[i], [k]: v }; setScenes(next); };
  const regen = async (i) => {
    setBusy(scenes[i].index);
    try { const { data } = await api.post(`/projects/${pid}/scenes/regenerate/${scenes[i].index}`); const next = [...scenes]; next[i] = { ...next[i], url: data.url }; setScenes(next); toast.success("Scene regenerated"); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Image provider needed"); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500 mb-1">Reorder, edit, duplicate, delete or regenerate individual scenes. Perfect prep for when a video provider is connected.</div>
      {scenes.map((s, i) => (
        <GlassCard key={i} className="p-4" data-testid="scene-row">
          <div className="flex gap-4">
            <div className="w-40 shrink-0">
              <div className="aspect-video rounded-lg bg-[#0A1435] overflow-hidden">
                {s.url ? <img src={mediaUrl(s.url)} alt={s.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon className="w-5 h-5" /></div>}
              </div>
              <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500 font-mono">
                <span>Scene {s.index}</span><span>{s.duration_seconds}s · ~$0.04</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <input value={s.title || ""} onChange={(e) => editField(i, "title", e.target.value)} onBlur={() => persist(scenes)} data-testid="scene-title-input"
                className="w-full bg-transparent border-b border-white/10 focus:border-[#5B4DFF]/60 outline-none text-white text-sm font-medium pb-1 mb-2" />
              <textarea value={s.description || ""} onChange={(e) => editField(i, "description", e.target.value)} onBlur={() => persist(scenes)} rows={2}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-2 outline-none text-slate-300 text-xs resize-none" />
              <div className="flex items-center gap-1.5 mt-2">
                <SceneBtn onClick={() => regen(i)} icon={busy === s.index ? Loader2 : RotateCw} label="Regenerate" spin={busy === s.index} testid={`scene-regen-${s.index}`} />
                <SceneBtn onClick={() => dup(i)} icon={Copy} label="Duplicate" />
                <SceneBtn onClick={() => move(i, -1)} icon={ArrowUp} />
                <SceneBtn onClick={() => move(i, 1)} icon={ArrowDown} />
                <SceneBtn onClick={() => del(i)} icon={Trash2} danger />
              </div>
            </div>
          </div>
        </GlassCard>
      ))}
    </div>
  );
}

function SceneBtn({ onClick, icon: Icon, label, danger, spin, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={cx("inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border bg-white/5 border-white/10", danger ? "text-slate-400 hover:text-red-400" : "text-slate-300 hover:text-white")}>
      <Icon className={cx("w-3.5 h-3.5", spin && "animate-spin")} /> {label}
    </button>
  );
}

function Clips({ pid, data, onChange }) {
  const [busy, setBusy] = useState(null);
  if (!data?.videos) return <GlassCard className="p-8 text-center text-slate-500">
    No rendered clips yet. Connect a video provider (fal.ai) and generate — clips appear here.
  </GlassCard>;
  const retry = async (i) => {
    setBusy(i);
    try { await api.post(`/projects/${pid}/scenes/regenerate-video/${i}`); toast.success("Re-rendering scene in background — refresh in a minute"); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Re-render failed"); }
    finally { setBusy(null); }
  };
  return (
    <div>
      <div className="text-xs text-slate-500 mb-3">Rendered by {data.provider} · {data.mode} mode. Failed clips regenerate individually — never the whole project.</div>
      <div className="grid sm:grid-cols-2 gap-4">
        {data.videos.map((v) => (
          <GlassCard key={v.index} className="overflow-hidden" data-testid="clip-card">
            <div className="aspect-video bg-black">
              {v.url ? <video src={mediaUrl(v.url)} controls className="w-full h-full object-cover" data-testid="clip-video" />
                : <div className="w-full h-full grid place-items-center text-slate-600"><Clapperboard className="w-6 h-6" /></div>}
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="text-sm text-white truncate">Scene {v.index}: {v.title}</div>
              <div className="flex items-center gap-2 shrink-0">
                {v.status === "rendering" ? <Pill tone="gold">Rendering…</Pill> : v.url ? <Pill tone="green">Rendered</Pill> : <Pill tone="red">Failed</Pill>}
                <button onClick={() => retry(v.index)} disabled={busy === v.index} data-testid={`clip-retry-${v.index}`} title="Re-render this scene"
                  className="inline-flex items-center gap-1 text-xs text-[#FFB300] disabled:opacity-50">
                  {busy === v.index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

function AudioTab({ voiceover, music }) {
  if (!voiceover && !music) return <Pending />;
  return (
    <div className="space-y-4">
      <GlassCard className="p-6">
        <Section title="Voice Narration (OpenAI TTS — real)">
          {voiceover?.url ? (
            <div className="space-y-2">
              <audio controls src={mediaUrl(voiceover.url)} className="w-full" data-testid="voiceover-audio" />
              <div className="text-xs text-slate-500">Voice: {voiceover.voice} · ~{voiceover.duration_estimate}s · speed {voiceover.speed}</div>
            </div>
          ) : <p className="text-sm text-slate-500">Narration not generated.</p>}
        </Section>
      </GlassCard>
      <GlassCard className="p-6">
        <Section title="Background Music (royalty-free)">
          {music?.url ? (
            <div className="space-y-2">
              <audio controls src={music.url} className="w-full" data-testid="music-audio" />
              <div className="text-xs text-slate-500">{music.title} · {music.license} · auto-ducks under narration</div>
            </div>
          ) : <p className="text-sm text-slate-500">No music track.</p>}
        </Section>
      </GlassCard>
    </div>
  );
}

function Exports({ pid }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${pid}/exports`).then(({ data }) => setData(data)).catch(() => {}); }, [pid]);
  if (!data) return <Pending />;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {data.exports.map((e) => (
        <GlassCard key={e.platform} className={cx("p-5", e.future && "opacity-70")} data-testid="export-card">
          <div className="flex items-center justify-between mb-3">
            <div><div className="text-white font-medium">{e.platform}</div><div className="text-xs text-slate-500">{e.aspect} · {e.resolution}</div></div>
            {e.future ? <Pill tone="slate">Future</Pill> : <Pill tone={e.status === "ready_to_render" ? "green" : "gold"}>{e.status === "ready_to_render" ? "Ready" : "Awaiting provider"}</Pill>}
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>~{e.estimated_size_mb} MB</span><span>~{e.estimated_time_seconds}s</span>
          </div>
        </GlassCard>
      ))}
    </div>
  );
}
